import { jsPDF } from 'jspdf';
import { BenchmarkComparisonView } from './vessel-insights-metrics.util';
import { ScoreBreakdownItem } from './vessel-insights-metrics.util';

export interface AiAnalysisPdfInput {
  vesselName: string;
  imo: string;
  port: string;
  operation: string;
  generatedAt: string;
  executiveSummary: string;
  aiInsights: string[];
  guidance: string[];
  chiefEngineer: string;
  technicalSuperintendent: string;
  navalArchitect: string;
  operationalScore: number;
  aiConfidence: number;
  benchmark: BenchmarkComparisonView;
  scoreBreakdown: ScoreBreakdownItem[];
  riskIndicators: Array<{ label: string; percent: number }>;
}

export function downloadAiAnalysisPdf(input: AiAnalysisPdfInput): void {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const margin = 14;
  let y = margin;
  const lineHeight = 6;
  const pageWidth = pdf.internal.pageSize.getWidth();
  const maxWidth = pageWidth - margin * 2;

  const addLine = (text: string, size = 10, bold = false) => {
    pdf.setFont('helvetica', bold ? 'bold' : 'normal');
    pdf.setFontSize(size);
    const lines = pdf.splitTextToSize(text, maxWidth);
    for (const line of lines) {
      if (y > 280) {
        pdf.addPage();
        y = margin;
      }
      pdf.text(line, margin, y);
      y += lineHeight;
    }
  };

  const addGap = (gap = 4) => {
    y += gap;
  };

  addLine('AI Vessel Performance Analysis', 16, true);
  addGap(2);
  addLine(`Vessel: ${input.vesselName} | IMO: ${input.imo}`);
  addLine(`Port: ${input.port} | Operation: ${input.operation}`);
  addLine(`Generated: ${input.generatedAt}`);
  addGap(4);

  addLine('Executive Summary', 12, true);
  addLine(input.executiveSummary);
  addGap(3);

  addLine('Benchmark Comparison', 12, true);
  if (input.benchmark.hasData) {
    addLine(
      `Actual Consumption: ${input.benchmark.actualMt ?? 'N/A'} MT`,
    );
    addLine(
      `TCP Benchmark: ${input.benchmark.tcpMt ?? 'N/A'} MT` +
        (input.benchmark.tcpVariancePct != null
          ? ` (${input.benchmark.tcpVariancePct > 0 ? '+' : ''}${input.benchmark.tcpVariancePct}%)`
          : ''),
    );
    addLine(
      `Technical Baseline: ${input.benchmark.baselineMt ?? 'N/A'} MT` +
        (input.benchmark.baselineVariancePct != null
          ? ` (${input.benchmark.baselineVariancePct > 0 ? '+' : ''}${input.benchmark.baselineVariancePct}%)`
          : ''),
    );
  } else {
    addLine('Benchmark data not available in extracted report.');
  }
  addGap(3);

  addLine('Operational Score', 12, true);
  addLine(`Final Operational Score: ${input.operationalScore}%`);
  addLine(`AI Confidence: ${input.aiConfidence}%`);
  input.scoreBreakdown.forEach((item) => {
    const sign = item.impact > 0 ? '+' : '';
    addLine(`${item.label}: ${sign}${item.impact}`);
  });
  addGap(3);

  addLine('Risk Indicators', 12, true);
  input.riskIndicators.forEach((risk) => {
    addLine(`${risk.label}: ${risk.percent}%`);
  });
  addGap(3);

  addLine('AI Key Findings', 12, true);
  input.aiInsights.forEach((finding) => addLine(`• ${finding}`));
  addGap(3);

  addLine('Recommendations', 12, true);
  input.guidance.forEach((item) => addLine(`• ${item}`));
  addGap(3);

  addLine('Expert Commentary', 12, true);
  addLine(`Chief Engineer: ${input.chiefEngineer}`);
  addLine(`Technical Superintendent: ${input.technicalSuperintendent}`);
  addLine(`Naval Architect: ${input.navalArchitect}`);

  const safeName = (input.vesselName || 'vessel').replace(/\s+/g, '-').toLowerCase();
  pdf.save(`${safeName}-ai-analysis.pdf`);
}
